import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Eye, ArrowLeft, ChevronDown, AlertTriangle, Copy, Download, Loader2, Settings, LogOut, Lock, Briefcase, Cloud, Users, Truck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { lock: Lock, briefcase: Briefcase, cloud: Cloud, users: Users, truck: Truck };

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2">
          <Eye className="w-6 h-6 text-brand" strokeWidth={2} />
          <span className="font-heading font-black text-lg tracking-tight text-zinc-900">ContractEye</span>
        </Link>
        <div className="flex items-center gap-3">
          {user ? (
            <>
              <Link to="/dashboard" className="font-body text-sm text-zinc-600 hover:text-zinc-900">Dashboard</Link>
              <Link to="/settings" className="p-2 hover:bg-zinc-100"><Settings className="w-4 h-4 text-zinc-600" /></Link>
              <button onClick={async () => { await logout(); navigate('/'); }} className="p-2 hover:bg-zinc-100"><LogOut className="w-4 h-4 text-zinc-600" /></button>
            </>
          ) : (
            <>
              <Link to="/login" className="font-body text-sm text-zinc-600">Log In</Link>
              <Link to="/signup" className="bg-brand text-white px-5 py-2 font-body font-medium text-sm">Get Started</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function SeverityBadge({ severity }) {
  const s = severity?.toLowerCase();
  if (s === 'high') return <span className="bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">High</span>;
  if (s === 'medium') return <span className="bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">Medium</span>;
  return <span className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-bold uppercase tracking-wider">Low</span>;
}

function RiskScoreDisplay({ score }) {
  let color, label;
  if (score >= 7) { color = 'text-rose-600'; label = 'High Risk'; }
  else if (score >= 4) { color = 'text-amber-600'; label = 'Moderate Risk'; }
  else { color = 'text-emerald-600'; label = 'Low Risk'; }
  return (
    <div className="text-center" data-testid="template-risk-score">
      <div className={`font-mono font-bold text-6xl ${color}`}>{score}</div>
      <div className="font-mono text-sm text-zinc-400 mt-1">/ 10</div>
      <div className={`font-body text-sm font-bold mt-2 ${color}`}>{label}</div>
    </div>
  );
}

function CollapsibleSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-zinc-200 bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-zinc-50 transition-colors">
        <span className="font-heading font-bold text-sm text-zinc-900">{title}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-6 pb-6 border-t border-zinc-100">{children}</div>}
    </div>
  );
}

function CopyButton({ text, label }) {
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); }}
      className="flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-600 transition-colors">
      <Copy className="w-3 h-3" /> Copy
    </button>
  );
}

export default function TemplateViewPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/templates/${slug}`);
        setTemplate(res.data.template);
        setReport(res.data.report);
      } catch {
        toast.error('Template not found');
        navigate('/templates');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-brand animate-spin" />
      </div>
    );
  }

  if (!report || !template) return null;

  const clauses = report.extracted_clauses || {};
  const clauseLabels = {
    payment_terms: 'Payment Terms', termination: 'Termination', liability: 'Liability',
    indemnity: 'Indemnity', confidentiality: 'Confidentiality', exclusivity: 'Exclusivity',
    renewal: 'Renewal', governing_law: 'Governing Law', dispute_resolution: 'Dispute Resolution',
    ip_ownership: 'IP Ownership', non_compete: 'Non-Compete',
  };
  const Icon = ICONS[template.icon] || Lock;

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="template-view-page">
      <Navbar />
      <main className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Link to="/templates" className="flex items-center gap-1 font-body text-sm text-zinc-500 hover:text-zinc-700 mb-6" data-testid="template-back">
            <ArrowLeft className="w-4 h-4" /> All Templates
          </Link>

          {/* Template header */}
          <div className="bg-brand text-white p-6 mb-6" data-testid="template-header">
            <div className="flex items-center gap-3 mb-3">
              <Icon className="w-6 h-6 text-white/80" strokeWidth={1.5} />
              <span className="text-xs font-bold tracking-[0.2em] uppercase text-white/60 font-body">Template Analysis</span>
            </div>
            <h1 className="font-heading font-bold text-2xl tracking-tight mb-2">{template.title}</h1>
            <p className="font-body text-sm text-white/80 mb-4">{template.description}</p>
            <div className="flex flex-wrap gap-2">
              {template.focus_areas.map((f, i) => (
                <span key={i} className="bg-white/10 border border-white/20 text-white px-2.5 py-1 text-xs font-body">{f}</span>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 px-4 py-3 mb-6">
            <p className="font-body text-xs text-amber-800">
              <strong>Disclaimer:</strong> This is a sample analysis of a template contract. It provides risk insights only and does not constitute legal advice.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-4">
              <CollapsibleSection title="Executive Summary">
                <div className="flex justify-end mb-2"><CopyButton text={report.executive_summary} label="Summary" /></div>
                <p className="font-body text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap mt-2" data-testid="template-summary">{report.executive_summary}</p>
              </CollapsibleSection>

              <CollapsibleSection title="Key Obligations">
                <ul className="space-y-2 mt-2" data-testid="template-obligations">
                  {(report.key_obligations || []).map((o, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="font-mono text-xs text-zinc-400 mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <span className="font-body text-sm text-zinc-700">{o}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>

              <CollapsibleSection title={`Risk Flags (${(report.risk_flags || []).length})`}>
                <div className="space-y-3 mt-2" data-testid="template-risk-flags">
                  {(report.risk_flags || []).sort((a, b) => {
                    const order = { High: 0, Medium: 1, Low: 2 };
                    return (order[a.severity] ?? 3) - (order[b.severity] ?? 3);
                  }).map((f, i) => (
                    <div key={i} className="border border-zinc-100 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <SeverityBadge severity={f.severity} />
                        <span className="font-heading font-bold text-sm text-zinc-900">{f.category}</span>
                      </div>
                      {f.clause_excerpt && <p className="font-mono text-xs text-zinc-500 bg-zinc-50 p-2 mb-2 border-l-2 border-zinc-200 italic">"{f.clause_excerpt}"</p>}
                      <p className="font-body text-sm text-zinc-700">{f.concern}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Suggested Revisions">
                <div className="flex justify-end mb-2"><CopyButton text={(report.suggested_revisions || []).join('\n')} label="Revisions" /></div>
                <ul className="space-y-2 mt-2" data-testid="template-revisions">
                  {(report.suggested_revisions || []).map((r, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="font-mono text-xs text-zinc-400 mt-0.5 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                      <span className="font-body text-sm text-zinc-700">{r}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>

              <CollapsibleSection title="Extracted Clauses" defaultOpen={false}>
                <div className="grid md:grid-cols-2 gap-3 mt-2" data-testid="template-clauses">
                  {Object.entries(clauseLabels).map(([key, label]) => {
                    const val = clauses[key];
                    if (!val) return null;
                    return (
                      <div key={key} className="border border-zinc-100 p-3">
                        <span className="font-body text-xs font-bold uppercase tracking-wider text-zinc-500 block mb-1">{label}</span>
                        <p className="font-body text-xs text-zinc-700 leading-relaxed">{val}</p>
                      </div>
                    );
                  })}
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="What to Review with a Lawyer">
                <div className="bg-blue-50 border border-blue-200 p-4 mt-2" data-testid="template-lawyer-review">
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                    <p className="font-body text-xs text-blue-800 font-medium">We recommend discussing these points with a qualified attorney before signing this type of contract.</p>
                  </div>
                  <ul className="space-y-2">
                    {(report.lawyer_review_points || []).map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="font-mono text-xs text-blue-400 mt-0.5 shrink-0">{i + 1}.</span>
                        <span className="font-body text-xs text-blue-800">{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CollapsibleSection>

              {/* CTA */}
              <div className="bg-brand p-8 text-center" data-testid="template-cta">
                <h3 className="font-heading font-bold text-xl text-white mb-2">Have a {template.title.toLowerCase()} to review?</h3>
                <p className="font-body text-sm text-white/80 mb-6">Upload your own contract for a personalized risk analysis.</p>
                <Link to="/dashboard" className="inline-block bg-white text-brand hover:bg-zinc-100 px-8 py-3 font-body font-medium text-sm transition-colors" data-testid="template-scan-own-btn">
                  Scan Your Contract
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-4 space-y-4">
              <div className="bg-white border border-zinc-200 p-6 lg:sticky lg:top-24" data-testid="template-sidebar">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 font-body mb-4">Risk Assessment</p>
                <RiskScoreDisplay score={report.risk_score} />
                <div className="mt-6 pt-6 border-t border-zinc-100">
                  <p className="font-body text-xs font-medium text-zinc-700 mb-3">Risk Distribution</p>
                  <div className="space-y-2">
                    {['High', 'Medium', 'Low'].map(s => {
                      const count = (report.risk_flags || []).filter(f => f.severity === s).length;
                      return (
                        <div key={s} className="flex items-center justify-between">
                          <SeverityBadge severity={s} />
                          <span className="font-mono text-xs text-zinc-600">{count} flag{count !== 1 ? 's' : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-zinc-100">
                  <p className="font-body text-xs font-medium text-zinc-700 mb-2">Focus Areas</p>
                  <div className="flex flex-wrap gap-1.5">
                    {template.focus_areas.map((f, i) => (
                      <span key={i} className="bg-zinc-100 text-zinc-700 px-2 py-0.5 text-xs font-body">{f}</span>
                    ))}
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-zinc-100">
                  <p className="font-body text-xs font-medium text-zinc-700 mb-2">Clauses Found</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(clauseLabels).map(([key, label]) => (
                      <span key={key} className={`px-2 py-0.5 text-xs font-body ${clauses[key] ? 'bg-zinc-100 text-zinc-700' : 'bg-zinc-50 text-zinc-300 line-through'}`}>{label}</span>
                    ))}
                  </div>
                </div>
                <div className="mt-6">
                  <Link to="/dashboard" className="block w-full text-center bg-brand text-white hover:bg-brand-hover py-2.5 font-body text-sm font-medium transition-colors" data-testid="sidebar-scan-btn">
                    Scan Your Own Contract
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
